import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  AccountBalance,
  SportsEsports,
  MonetizationOn,
  Timeline,
  AttachMoney,
  Refresh,
  ShowChart,
  PieChartOutline,
  BarChartOutlined,
  Warning,
  CheckCircle,
  ArrowUpward,
  ArrowDownward,
  SwapVert
} from '@mui/icons-material';
import {
  LineChart, Line,
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { adminAPI } from '../services/api';
import api from '../services/api';
import { formatNumber, formatCurrency } from '../utils/formatters';

// Цветовая палитра
const COLORS = {
  primary: '#8a2be2',
  secondary: '#9932cc',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
  purple: '#9c27b0',
  indigo: '#3f51b5',
  pink: '#e91e63',
  teal: '#009688'
};

const CHART_COLORS = ['#8a2be2', '#9932cc', '#da70d6', '#dda0dd', '#ee82ee'];

// Компонент статистической карточки
const StatCard = ({ title, value, subtitle, change, todayCount, icon, color = COLORS.primary, loading = false }) => {
  const isPositive = change >= 0;

  return (
    <Card sx={{
      background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
      border: `1px solid ${color}44`,
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}33`
      }
    }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" height={40} />
            <Skeleton variant="text" width="40%" />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  {title}
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', my: 1 }}>
                  {value}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    {subtitle}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  {isPositive ?
                    <TrendingUp sx={{ color: COLORS.success, mr: 0.5, fontSize: 20 }} /> :
                    <TrendingDown sx={{ color: COLORS.error, mr: 0.5, fontSize: 20 }} />
                  }
                  <Typography
                    variant="body2"
                    sx={{ color: isPositive ? COLORS.success : COLORS.error }}
                  >
                    {isPositive ? '+' : ''}{change}%
                  </Typography>
                  {todayCount !== undefined && (
                    <Chip
                      label={`+${todayCount}`}
                      size="small"
                      sx={{
                        ml: 0.5,
                        bgcolor: COLORS.success + '22',
                        color: COLORS.success,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: 20
                      }}
                    />
                  )}
                  <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>
                    за сегодня
                  </Typography>
                </Box>
              </Box>
              <Box sx={{
                background: color,
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {React.cloneElement(icon, { sx: { color: 'white', fontSize: 30 } })}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Компонент распределения VIP-статусов
const VipDistribution = ({ distribution = {} }) => {
  const vipLevels = [
    { key: 'platinum', label: 'Platinum VIP', color: '#e5e7eb', icon: '💎' },
    { key: 'diamond', label: 'Diamond VIP', color: '#b9f2ff', icon: '💠' },
    { key: 'gold', label: 'Gold VIP', color: '#FFD700', icon: '🥇' },
    { key: 'silver', label: 'Silver VIP', color: '#C0C0C0', icon: '🥈' },
    { key: 'bronze', label: 'Bronze VIP', color: '#CD7F32', icon: '🥉' },
    { key: 'none', label: 'Без VIP', color: '#6b7280', icon: '👤' }
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Распределение VIP-статусов
        </Typography>
        <Box sx={{ mt: 2 }}>
          {vipLevels.map((level) => {
            const data = distribution[level.key] || { count: 0, percentage: 0 };
            return (
              <Box key={level.key} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1 }}>{level.icon}</Typography>
                    <Typography variant="body2">{level.label}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {data.count}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 45, textAlign: 'right' }}>
                      {data.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={data.percentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: `${level.color}22`,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: level.color,
                      borderRadius: 4
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

// Основной компонент Dashboard
function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalBalance: 0,
    totalStaked: 0,
    totalClaims: 0,
    totalTransactions: 0,
    dailyRevenue: 0,
    weeklyRevenue: 0
  });
  const [chartData, setChartData] = useState([]);
  const [distributionData, setDistributionData] = useState([]);
  const [vipDistribution, setVipDistribution] = useState({});
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState({
    database: 'operational',
    api: 'operational',
    telegram: 'operational',
    ton: 'operational'
  });
  // 🔒 ВХОД/ВЫХОД - Метрика реальных денег
  const [moneyFlow, setMoneyFlow] = useState({
    summary: { totalDeposits: 0, totalWithdrawals: 0, netFlow: 0, netFlowPercent: 0, depositCount: 0, withdrawalCount: 0 },
    chartData: []
  });

  // Фильтры для графика
  const [chartFilters, setChartFilters] = useState({
    deposits_SPERM: true,
    withdrawals_SPERM: true,
    deposits_USDT: true,
    withdrawals_USDT: true,
    stakings_lunar: true,
    stakings_star: true,
    stakings_marathon: true
  });

  // Выбранный период для графика
  const [chartPeriod, setChartPeriod] = useState('week');

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Обновление каждые 30 секунд
    return () => clearInterval(interval);
  }, [chartPeriod]);

  const [enhancedData, setEnhancedData] = useState({
    today: {
      deposits: { SPERM: { amount: 0, count: 0 }, USDT: { amount: 0, count: 0 } },
      withdrawals: { SPERM: { amount: 0, count: 0 }, USDT: { amount: 0, count: 0 } }
    },
    moneyFlow: [],
    usersHistory: [],
    newUsersToday: 0,
    referrals: {
      total: { SPERM: { oneTime: 0, daily: 0, total: 0 }, USDT: { oneTime: 0, daily: 0, total: 0 } },
      today: { SPERM: { oneTime: 0, daily: 0, total: 0 }, USDT: { oneTime: 0, daily: 0, total: 0 } }
    }
  });

  const loadDashboardData = async () => {
    try {
      setRefreshing(true);

      // Флаг для отслеживания успешной загрузки расширенных данных
      let enhancedDataLoaded = false;

      // Загружаем расширенные данные из нового endpoint
      try {
        const enhancedResponse = await api.get(`/admin/dashboard/enhanced?period=${chartPeriod}`);
        if (enhancedResponse.data?.success && enhancedResponse.data?.data) {
          const data = enhancedResponse.data.data;
          enhancedDataLoaded = true;

          setEnhancedData({
            today: data.today || {},
            moneyFlow: data.moneyFlow || [],
            usersHistory: data.usersHistory || [],
            newUsersToday: data.newUsersToday || 0,
            firstDepsToday: data.firstDepsToday || 0,  // ПервоДепы сегодня
            referrals: data.referrals || {}
          });

          // Обновляем основные метрики с реальными данными за вчера
          setStats({
            totalUsers: data.totalUsers || 0,
            activeUsers: data.activeToday || 0,
            totalBalance: data.totalBalance || 0,
            totalStaked: data.totalStaked || 0,
            totalStakedInitial: data.totalStakedInitial || 0,  // Изначально вложено
            totalStakedVisual: data.totalStakedVisual || 0,    // Визуальный баланс
            // Реальные данные за вчера для расчёта процентов
            previousTotalUsers: data.previousTotalUsers || data.totalUsers || 0,
            previousActiveUsers: data.previousActiveUsers || data.activeToday || 0,
            previousTotalBalance: data.previousTotalBalance || data.totalBalance || 0,
            previousTotalStaked: data.previousTotalStaked || data.totalStaked || 0
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки расширенных данных:', error);
      }

      // Загружаем основные данные dashboard из правильного endpoint (fallback)
      const dashboardResponse = await adminAPI.getDashboard();

      if (dashboardResponse.data?.success && dashboardResponse.data?.data) {
        const data = dashboardResponse.data.data;

        // Обновляем статистику ТОЛЬКО если расширенные данные НЕ загрузились
        if (!enhancedDataLoaded) {
          setStats({
            totalUsers: data.users?.total || 0,
            activeUsers: data.users?.active || 0,
            newUsers: data.users?.new || 0,
            totalBalance: data.finance?.balance || 0,
            totalStaked: data.finance?.staked || 0,
            totalStakedVisual: data.finance?.stakedVisual || 0,
            totalTransactions: data.transactions?.count || 0,
            transactionVolume: data.transactions?.volume || 0,
            totalGames: data.games?.total || 0,
            gamesWon: data.games?.wins || 0,
            dailyRevenue: data.finance?.rewards || 0,
            weeklyRevenue: (data.finance?.rewards || 0) * 7,
            // Если нет данных за вчера - не показываем изменение (0%)
            previousTotalUsers: data.users?.total || 0,
            previousActiveUsers: data.users?.active || 0,
            previousTotalBalance: data.finance?.balance || 0,
            previousTotalStaked: data.finance?.staked || 0
          });
        }
        
        // Генерируем данные для графиков из полученных данных
        const chartDates = [];
        const chartValues = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          chartDates.push(date.toLocaleDateString('ru-RU', { month: 'short', day: 'numeric' }));
          chartValues.push(Math.floor(Math.random() * 50) + 10);
        }
        
        const mockChartData = {
          labels: chartDates,
          datasets: [{
            label: 'Пользователи',
            data: chartValues,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }]
        };
        setChartData(mockChartData);
        
        // Генерируем данные распределения
        const mockDistribution = [
          { name: 'Активные', value: data.users?.active || 0, color: COLORS.success },
          { name: 'Новые', value: data.users?.new || 0, color: COLORS.info },
          { name: 'Неактивные', value: Math.max(0, (data.users?.total || 0) - (data.users?.active || 0) - (data.users?.new || 0)), color: COLORS.warning }
        ];
        setDistributionData(mockDistribution);
        
        // Загружаем топ пользователей
        const usersResponse = await adminAPI.getUsers({ limit: 5, sort: 'balance' });
        if (usersResponse.data?.success && usersResponse.data?.data) {
          const users = usersResponse.data.data.users || usersResponse.data.data;
          setTopUsers(Array.isArray(users) ? users : []);
        }

        // Загружаем распределение VIP-статусов
        try {
          const vipResponse = await api.get('/admin/stats/vip-distribution');
          if (vipResponse.data?.success && vipResponse.data?.data) {
            setVipDistribution(vipResponse.data.data);
          }
        } catch (error) {
          console.error('Ошибка загрузки VIP распределения:', error);
        }

        // 🔒 Загружаем ВХОД/ВЫХОД - метрику реальных денег
        try {
          const moneyFlowResponse = await api.get('/admin/stats/money-flow?period=week');
          if (moneyFlowResponse.data?.success && moneyFlowResponse.data?.data) {
            setMoneyFlow(moneyFlowResponse.data.data);
          }
        } catch (error) {
          console.error('Ошибка загрузки денежного потока:', error);
        }
      }
      
      // Проверяем статус систем
      checkSystemStatus();
      
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  const checkSystemStatus = async () => {
    try {
      const response = await api.get('/admin/system/status');
      if (response.data) {
        setSystemStatus(response.data);
      }
    } catch (error) {
      console.error('Ошибка проверки статуса систем:', error);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  // Расчет изменений
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Заголовок с кнопкой обновления */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Последнее обновление: {lastUpdate.toLocaleTimeString('ru-RU')}
          </Typography>
        </Box>
        <Tooltip title="Обновить данные">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <Refresh className={refreshing ? 'rotating' : ''} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Статус систем */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={1}>
          {Object.entries(systemStatus).map(([system, status]) => (
            <Grid item key={system}>
              <Chip
                icon={status === 'operational' ? <CheckCircle /> : <Warning />}
                label={system.toUpperCase()}
                color={status === 'operational' ? 'success' : 'warning'}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Основные метрики */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего пользователей"
            value={formatNumber(stats.totalUsers)}
            change={calculateChange(stats.totalUsers, stats.previousTotalUsers)}
            todayCount={enhancedData.newUsersToday}
            icon={<People />}
            color={COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Активных сегодня"
            value={formatNumber(stats.activeUsers)}
            change={calculateChange(stats.activeUsers, stats.previousActiveUsers)}
            icon={<Timeline />}
            color={COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Общий баланс"
            value={formatNumber(stats.totalBalance)}
            change={calculateChange(stats.totalBalance, stats.previousTotalBalance)}
            icon={<AccountBalance />}
            color={COLORS.info}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="В стейкинге (реально)"
            value={formatNumber(stats.totalStaked)}
            subtitle={`Вложено: ${formatNumber(stats.totalStakedInitial || 0)} | Visual: ${formatNumber(stats.totalStakedVisual || 0)}`}
            change={calculateChange(stats.totalStaked, stats.previousTotalStaked)}
            icon={<MonetizationOn />}
            color={COLORS.warning}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ПервоДепы - отдельная метрика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${COLORS.pink}22 0%, ${COLORS.pink}11 100%)`,
            border: `1px solid ${COLORS.pink}44`,
            height: '100%',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${COLORS.pink}33`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    ПервоДепы сегодня
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', my: 1 }}>
                    {formatNumber(enhancedData.firstDepsToday || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Первое фин. действие (депозит/стейкинг)
                  </Typography>
                </Box>
                <Box sx={{
                  background: COLORS.pink,
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AttachMoney sx={{ color: 'white', fontSize: 30 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Объемы за текущий день */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{
            background: `linear-gradient(135deg, ${COLORS.success}11 0%, ${COLORS.success}05 100%)`,
            border: `1px solid ${COLORS.success}33`
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📥 Пополнения за сегодня
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">SPERM</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                      {formatNumber(enhancedData.today.deposits.SPERM.amount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Операций: {enhancedData.today.deposits.SPERM.count}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">USDT</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                      ${formatNumber(enhancedData.today.deposits.USDT.amount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Операций: {enhancedData.today.deposits.USDT.count}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{
            background: `linear-gradient(135deg, ${COLORS.error}11 0%, ${COLORS.error}05 100%)`,
            border: `1px solid ${COLORS.error}33`
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📤 Выводы за сегодня
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">SPERM</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                      {formatNumber(enhancedData.today.withdrawals.SPERM.amount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Операций: {enhancedData.today.withdrawals.SPERM.count}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">USDT</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                      ${formatNumber(enhancedData.today.withdrawals.USDT.amount)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Операций: {enhancedData.today.withdrawals.USDT.count}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* График роста пользователей + ПервоДепы */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People /> Рост пользователей
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Новые регистрации и ПервоДепы за выбранный период
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="textSecondary">Новых за период</Typography>
                    <Typography variant="h6" sx={{ color: COLORS.primary, fontWeight: 'bold' }}>
                      +{enhancedData.usersHistory.reduce((sum, day) => sum + day.newUsers, 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="textSecondary">ПервоДепов</Typography>
                    <Typography variant="h6" sx={{ color: COLORS.pink, fontWeight: 'bold' }}>
                      +{enhancedData.usersHistory.reduce((sum, day) => sum + (day.firstDeps || 0), 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={250} />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={enhancedData.usersHistory}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorFirstDeps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.pink} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.pink} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="date"
                      stroke="rgba(255,255,255,0.5)"
                      style={{ fontSize: 12 }}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.5)"
                      style={{ fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        background: 'rgba(30,41,59,0.95)',
                        border: `1px solid ${COLORS.primary}`,
                        borderRadius: 8,
                        color: '#fff'
                      }}
                      formatter={(value, name) => {
                        if (name === 'newUsers') return [`+${value}`, 'Новых пользователей'];
                        if (name === 'firstDeps') return [`+${value}`, 'ПервоДепов'];
                        return [value, name];
                      }}
                    />
                    <Legend
                      formatter={(value) => {
                        if (value === 'newUsers') return 'Новые пользователи';
                        if (value === 'firstDeps') return 'ПервоДепы';
                        return value;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorUsers)"
                      name="newUsers"
                    />
                    <Area
                      type="monotone"
                      dataKey="firstDeps"
                      stroke={COLORS.pink}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorFirstDeps)"
                      name="firstDeps"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Графики - ВХОД/ВЫХОД на всю ширину */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* 🔒 ВХОД/ВЫХОД - Метрика реальных денег */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapVert /> ВВОД / ВЫВОД
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Реальные блокчейн-транзакции за неделю
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="textSecondary">Чистый поток</Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: moneyFlow.summary.netFlow >= 0 ? COLORS.success : COLORS.error,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end'
                      }}
                    >
                      {moneyFlow.summary.netFlow >= 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                      {formatNumber(Math.abs(moneyFlow.summary.netFlow))} SPERM
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Метрики ВХОД, ВЫХОД, ОБОРОТ */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.success}22 0%, ${COLORS.success}11 100%)`,
                    border: `1px solid ${COLORS.success}44`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ArrowDownward sx={{ color: COLORS.success }} />
                      <Typography variant="body2" color="textSecondary">ВХОД (Депозиты)</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: COLORS.success, fontWeight: 'bold' }}>
                      {formatNumber(moneyFlow.summary.totalDeposits)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {moneyFlow.summary.depositCount} транзакций
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.error}22 0%, ${COLORS.error}11 100%)`,
                    border: `1px solid ${COLORS.error}44`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ArrowUpward sx={{ color: COLORS.error }} />
                      <Typography variant="body2" color="textSecondary">ВЫХОД (Выводы)</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: COLORS.error, fontWeight: 'bold' }}>
                      {formatNumber(moneyFlow.summary.totalWithdrawals)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {moneyFlow.summary.withdrawalCount} транзакций
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.info}22 0%, ${COLORS.info}11 100%)`,
                    border: `1px solid ${COLORS.info}44`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SwapVert sx={{ color: COLORS.info }} />
                      <Typography variant="body2" color="textSecondary">ОБОРОТ</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: COLORS.info, fontWeight: 'bold' }}>
                      {formatNumber((moneyFlow.summary.totalDeposits || 0) + (moneyFlow.summary.totalWithdrawals || 0))}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(moneyFlow.summary.depositCount || 0) + (moneyFlow.summary.withdrawalCount || 0)} транзакций
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Выбор периода и фильтры графика */}
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={chartPeriod}
                    onChange={(e) => setChartPeriod(e.target.value)}
                    sx={{
                      backgroundColor: 'rgba(30,41,59,0.8)',
                      color: '#fff',
                      '& .MuiSelect-icon': { color: '#fff' }
                    }}
                  >
                    <MenuItem value="day">День</MenuItem>
                    <MenuItem value="week">Неделя</MenuItem>
                    <MenuItem value="month">Месяц</MenuItem>
                    <MenuItem value="half_year">Полгода</MenuItem>
                    <MenuItem value="year">Год</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary" sx={{ alignSelf: 'center' }}>
                  Показать:
                </Typography>
                <FormGroup row sx={{ gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={chartFilters.deposits_SPERM}
                        onChange={(e) => setChartFilters(prev => ({ ...prev, deposits_SPERM: e.target.checked }))}
                        sx={{ color: '#00ff00', '&.Mui-checked': { color: '#00ff00' } }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Ввод SPERM</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={chartFilters.withdrawals_SPERM}
                        onChange={(e) => setChartFilters(prev => ({ ...prev, withdrawals_SPERM: e.target.checked }))}
                        sx={{ color: '#ff0000', '&.Mui-checked': { color: '#ff0000' } }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Вывод SPERM</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={chartFilters.deposits_USDT}
                        onChange={(e) => setChartFilters(prev => ({ ...prev, deposits_USDT: e.target.checked }))}
                        sx={{ color: '#26a69a', '&.Mui-checked': { color: '#26a69a' } }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Ввод USDT</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={chartFilters.withdrawals_USDT}
                        onChange={(e) => setChartFilters(prev => ({ ...prev, withdrawals_USDT: e.target.checked }))}
                        sx={{ color: '#ef5350', '&.Mui-checked': { color: '#ef5350' } }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Вывод USDT</Typography>}
                  />
                </FormGroup>
                <Box sx={{ borderLeft: '1px solid rgba(255,255,255,0.2)', pl: 2, ml: 1 }}>
                  <FormGroup row sx={{ gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={chartFilters.stakings_lunar}
                          onChange={(e) => setChartFilters(prev => ({ ...prev, stakings_lunar: e.target.checked }))}
                          sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Lunar</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={chartFilters.stakings_star}
                          onChange={(e) => setChartFilters(prev => ({ ...prev, stakings_star: e.target.checked }))}
                          sx={{ color: '#ffc107', '&.Mui-checked': { color: '#ffc107' } }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Star</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={chartFilters.stakings_marathon}
                          onChange={(e) => setChartFilters(prev => ({ ...prev, stakings_marathon: e.target.checked }))}
                          sx={{ color: '#2196f3', '&.Mui-checked': { color: '#2196f3' } }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Marathon</Typography>}
                    />
                  </FormGroup>
                </Box>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={enhancedData.moneyFlow}>
                    <defs>
                      <linearGradient id="colorDepositsSperm" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00ff00" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#00ff00" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorWithdrawalsSperm" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ff0000" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ff0000" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorDepositsUsdt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#26a69a" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#26a69a" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorWithdrawalsUsdt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef5350" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ef5350" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorLunar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9c27b0" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#9c27b0" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorStar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ffc107" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ffc107" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorMarathon" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="date"
                      stroke="rgba(255,255,255,0.5)"
                      style={{ fontSize: 12 }}
                      textAnchor="middle"
                      tick={{ textAnchor: 'middle' }}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.5)"
                      style={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        background: 'rgba(30,41,59,0.95)',
                        border: `1px solid ${COLORS.primary}`,
                        borderRadius: 8,
                        color: '#fff'
                      }}
                      formatter={(value, name) => {
                        const labels = {
                          'deposits_SPERM': 'Ввод SPERM',
                          'deposits_USDT': 'Ввод USDT',
                          'withdrawals_SPERM': 'Вывод SPERM',
                          'withdrawals_USDT': 'Вывод USDT',
                          'stakings_lunar': 'Lunar стейкинги',
                          'stakings_star': 'Star стейкинги',
                          'stakings_marathon': 'Marathon стейкинги'
                        };
                        return [formatNumber(value), labels[name] || name];
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '10px',
                        fontSize: '12px'
                      }}
                      formatter={(value, entry) => {
                        const labels = {
                          'deposits_SPERM': 'Ввод SPERM',
                          'deposits_USDT': 'Ввод USDT',
                          'withdrawals_SPERM': 'Вывод SPERM',
                          'withdrawals_USDT': 'Вывод USDT',
                          'stakings_lunar': 'Lunar',
                          'stakings_star': 'Star',
                          'stakings_marathon': 'Marathon'
                        };
                        return labels[value] || value;
                      }}
                    />
                    {/* SPERM */}
                    {chartFilters.deposits_SPERM && (
                      <Area type="monotone" dataKey="deposits_SPERM" stroke="#00ff00" fillOpacity={1} fill="url(#colorDepositsSperm)" name="deposits_SPERM" />
                    )}
                    {chartFilters.withdrawals_SPERM && (
                      <Area type="monotone" dataKey="withdrawals_SPERM" stroke="#ff0000" fillOpacity={1} fill="url(#colorWithdrawalsSperm)" name="withdrawals_SPERM" />
                    )}
                    {/* USDT */}
                    {chartFilters.deposits_USDT && (
                      <Area type="monotone" dataKey="deposits_USDT" stroke="#26a69a" fillOpacity={1} fill="url(#colorDepositsUsdt)" name="deposits_USDT" />
                    )}
                    {chartFilters.withdrawals_USDT && (
                      <Area type="monotone" dataKey="withdrawals_USDT" stroke="#ef5350" fillOpacity={1} fill="url(#colorWithdrawalsUsdt)" name="withdrawals_USDT" />
                    )}
                    {/* Стейкинги */}
                    {chartFilters.stakings_lunar && (
                      <Area type="monotone" dataKey="stakings_lunar" stroke="#9c27b0" fillOpacity={1} fill="url(#colorLunar)" name="stakings_lunar" />
                    )}
                    {chartFilters.stakings_star && (
                      <Area type="monotone" dataKey="stakings_star" stroke="#ffc107" fillOpacity={1} fill="url(#colorStar)" name="stakings_star" />
                    )}
                    {chartFilters.stakings_marathon && (
                      <Area type="monotone" dataKey="stakings_marathon" stroke="#2196f3" fillOpacity={1} fill="url(#colorMarathon)" name="stakings_marathon" />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Таблица топ пользователей и активность */}
      <Grid container spacing={3}>
        {/* Топ пользователей */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Топ пользователей
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Пользователь</TableCell>
                        <TableCell>Ранг</TableCell>
                        <TableCell align="right">Баланс</TableCell>
                        <TableCell align="right">В стейкинге</TableCell>
                        <TableCell align="center">VIP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topUsers.map((user, index) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {user.username || `User ${user.telegram_id}`}
                              </Typography>
                              {index < 3 && (
                                <Chip 
                                  label={`#${index + 1}`} 
                                  size="small" 
                                  sx={{ 
                                    ml: 1,
                                    bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.rank} 
                              size="small"
                              sx={{
                                bgcolor: `${COLORS.primary}22`,
                                color: COLORS.primary
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(user.balance)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(user.total_staked || 0)}
                          </TableCell>
                          <TableCell align="center">
                            {user.vip_level > 0 ? (
                              <Chip
                                label={`VIP ${user.vip_level}`}
                                size="small"
                                color="warning"
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Распределение VIP-статусов */}
        <Grid item xs={12} md={5}>
          <VipDistribution distribution={vipDistribution} />
        </Grid>
      </Grid>

      {/* Реферальная программа */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                👥 Реферальная программа
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Всего выплачено */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.purple}11 0%, ${COLORS.purple}05 100%)`,
                    border: `1px solid ${COLORS.purple}33`
                  }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Всего заработано
                    </Typography>

                    {/* SPERM */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.primary }}>
                        {formatNumber(enhancedData.referrals.total.SPERM.total)} SPERM
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          Единоразовые: {formatNumber(enhancedData.referrals.total.SPERM.oneTime)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Ежедневные: {formatNumber(enhancedData.referrals.total.SPERM.daily)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* USDT */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.success }}>
                        ${formatNumber(enhancedData.referrals.total.USDT.total)} USDT
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          Единоразовые: ${formatNumber(enhancedData.referrals.total.USDT.oneTime)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Ежедневные: ${formatNumber(enhancedData.referrals.total.USDT.daily)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Выплачено за сегодня */}
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    p: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.info}11 0%, ${COLORS.info}05 100%)`,
                    border: `1px solid ${COLORS.info}33`
                  }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Заработано за сегодня
                    </Typography>

                    {/* SPERM */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.primary }}>
                        {formatNumber(enhancedData.referrals.today.SPERM.total)} SPERM
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          Единоразовые: {formatNumber(enhancedData.referrals.today.SPERM.oneTime)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Ежедневные: {formatNumber(enhancedData.referrals.today.SPERM.daily)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* USDT */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.success }}>
                        ${formatNumber(enhancedData.referrals.today.USDT.total)} USDT
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="textSecondary">
                          Единоразовые: ${formatNumber(enhancedData.referrals.today.USDT.oneTime)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Ежедневные: ${formatNumber(enhancedData.referrals.today.USDT.daily)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CSS для анимации вращения */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
}

export default Dashboard;